import type { GetV2ProviderScheduleEventsApiArg } from '@store/generated/appointments';
import type { GetV2ProviderAvailabilityRulesApiResponse } from '@store/generated/availability';
import { enhancedApi as availabilityApi } from '@store/generated/availability';

import type {
  AvailabilityRule,
  CreateAvailabilityRuleRequest,
  ListBlockedTimeRulesArgs,
  UpdateAvailabilityRuleRequest,
} from '../types/blockedTime';

import { appointmentsApi } from './api';

const BLOCKED_TIME_TAG_TYPES = ['BlockedTime', 'BlockedTimeList'] as const;
const AVAILABILITY_RULES_PAGE_SIZE = 1000;

interface RootStateWithScheduleQueries {
  api: {
    queries: Record<
      string,
      {
        originalArgs: GetV2ProviderScheduleEventsApiArg;
      }
    >;
  };
}

const getScheduleEventsQueryKeys = (queries: RootStateWithScheduleQueries['api']['queries']) =>
  Object.keys(queries).filter(key => key.startsWith('getV2ProviderScheduleEvents('));

const addMinutesIso = (startTime: string, durationMinutes: number): string =>
  new Date(new Date(startTime).getTime() + durationMinutes * 60_000).toISOString();

const filterBlockedRules = (
  rules: AvailabilityRule[],
  args: ListBlockedTimeRulesArgs,
): AvailabilityRule[] => {
  const providerIds = args.providerIds ? new Set(args.providerIds) : null;

  return rules.filter(rule => {
    if (rule.effect !== 'block') return false;
    if (!providerIds) return true;
    if (rule.provider_id == null) return args.includeOrgWide ?? true;
    return providerIds.has(rule.provider_id);
  });
};

export const blockedTimeApi = availabilityApi
  .enhanceEndpoints({ addTagTypes: BLOCKED_TIME_TAG_TYPES })
  .injectEndpoints({
    endpoints: builder => ({
      listBlockedTimeRules: builder.query<AvailabilityRule[], ListBlockedTimeRulesArgs | undefined>(
        {
          async queryFn(args, api) {
            const queryArgs = args ?? {};
            const allRules: AvailabilityRule[] = [];
            let page = 1;
            let total = 0;

            do {
              const request = api.dispatch(
                availabilityApi.endpoints.getV2ProviderAvailabilityRules.initiate(
                  { page, perPage: AVAILABILITY_RULES_PAGE_SIZE },
                  { forceRefetch: true, subscribe: false },
                ),
              );
              const result = await request;
              request.unsubscribe();

              if (result.error) {
                return { error: result.error };
              }

              const data = result.data as GetV2ProviderAvailabilityRulesApiResponse;
              allRules.push(...(data.items ?? []));
              total = data.total ?? allRules.length;
              page += 1;
            } while (allRules.length < total);

            return { data: filterBlockedRules(allRules, queryArgs) };
          },
          providesTags: result =>
            result
              ? [
                  ...result.map(rule => ({
                    type: 'BlockedTime' as const,
                    id: rule.availability_rule_id ?? 'unknown',
                  })),
                  { type: 'BlockedTimeList' as const, id: 'LIST' },
                ]
              : [{ type: 'BlockedTimeList' as const, id: 'LIST' }],
        },
      ),

      createBlockedTimeRule: builder.mutation<AvailabilityRule, CreateAvailabilityRuleRequest>({
        async queryFn(body, api) {
          const request = api.dispatch(
            availabilityApi.endpoints.postV2ProviderAvailabilityRules.initiate({
              createAvailabilityRuleRequest: body,
            }),
          );
          const result = await request;
          request.reset();

          if (result.error) {
            return { error: result.error };
          }

          const response = result.data;
          return { data: response.rule };
        },
        invalidatesTags: [{ type: 'BlockedTimeList', id: 'LIST' }],
      }),

      updateBlockedTimeRule: builder.mutation<
        AvailabilityRule,
        { availabilityRuleId: string; availabilityRule: UpdateAvailabilityRuleRequest }
      >({
        onQueryStarted: async ({ availabilityRuleId, availabilityRule }, mutationApi) => {
          const state = mutationApi.getState() as RootStateWithScheduleQueries;
          const queries = state.api?.queries || {};
          const patches = getScheduleEventsQueryKeys(queries).map(queryKey =>
            mutationApi.dispatch(
              appointmentsApi.util.updateQueryData(
                'getV2ProviderScheduleEvents',
                queries[queryKey].originalArgs,
                draft => {
                  for (const occurrence of draft.blocked_times ?? []) {
                    if (occurrence.availability_rule_id !== availabilityRuleId) continue;
                    if (occurrence.rrule || occurrence.rule.rrule) continue;

                    const startTime =
                      typeof availabilityRule.start_time === 'string'
                        ? availabilityRule.start_time
                        : occurrence.start_time;
                    const durationMinutes =
                      typeof availabilityRule.duration_minutes === 'number'
                        ? availabilityRule.duration_minutes
                        : occurrence.duration_minutes;
                    const providerId =
                      availabilityRule.provider_id !== undefined
                        ? availabilityRule.provider_id
                        : occurrence.provider_id;
                    const timezone =
                      typeof availabilityRule.timezone === 'string'
                        ? availabilityRule.timezone
                        : occurrence.timezone;

                    occurrence.start_time = startTime;
                    occurrence.duration_minutes = durationMinutes;
                    occurrence.end_time = addMinutesIso(startTime, durationMinutes);
                    occurrence.provider_id = providerId;
                    // Keep raw org-wide schedule events as `resource_id: null`.
                    // The calendar mapper fans that sentinel out to each visible provider column.
                    occurrence.resource_id =
                      providerId == null ? (occurrence.resource_id ?? null) : providerId;
                    occurrence.timezone = timezone;

                    if (availabilityRule.rrule !== undefined) {
                      occurrence.rrule = availabilityRule.rrule;
                    }
                    if (availabilityRule.end_date !== undefined) {
                      occurrence.end_date = availabilityRule.end_date;
                    }

                    occurrence.rule = {
                      ...occurrence.rule,
                      ...availabilityRule,
                      availability_rule_id: availabilityRuleId,
                      start_time: startTime,
                      duration_minutes: durationMinutes,
                      provider_id: providerId,
                      timezone,
                      scope: providerId == null ? 'organization' : 'user',
                    };
                  }
                },
              ),
            ),
          );

          try {
            await mutationApi.queryFulfilled;
          } catch {
            patches.forEach(patch => patch.undo());
          }
        },
        async queryFn({ availabilityRuleId, availabilityRule }, api) {
          const request = api.dispatch(
            availabilityApi.endpoints.patchV2ProviderAvailabilityRulesByAvailabilityRuleId.initiate(
              {
                availabilityRuleId,
                updateAvailabilityRuleRequest: availabilityRule,
              },
            ),
          );
          const result = await request;
          request.reset();

          if (result.error) {
            return { error: result.error };
          }

          const response = result.data;
          return { data: response.rule };
        },
        invalidatesTags: (result, error, { availabilityRuleId }) => [
          { type: 'BlockedTime', id: availabilityRuleId },
          { type: 'BlockedTimeList', id: 'LIST' },
        ],
      }),

      deleteBlockedTimeRule: builder.mutation<undefined, { availabilityRuleId: string }>({
        onQueryStarted: async ({ availabilityRuleId }, mutationApi) => {
          const state = mutationApi.getState() as RootStateWithScheduleQueries;
          const queries = state.api?.queries || {};
          const patches = getScheduleEventsQueryKeys(queries).map(queryKey =>
            mutationApi.dispatch(
              appointmentsApi.util.updateQueryData(
                'getV2ProviderScheduleEvents',
                queries[queryKey].originalArgs,
                draft => {
                  draft.blocked_times = (draft.blocked_times ?? []).filter(
                    occurrence => occurrence.availability_rule_id !== availabilityRuleId,
                  );
                },
              ),
            ),
          );

          try {
            await mutationApi.queryFulfilled;
          } catch {
            patches.forEach(patch => patch.undo());
          }
        },
        async queryFn({ availabilityRuleId }, api) {
          const request = api.dispatch(
            availabilityApi.endpoints.deleteV2ProviderAvailabilityRulesByAvailabilityRuleId.initiate(
              {
                availabilityRuleId,
              },
            ),
          );
          const result = await request;
          request.reset();

          if (result.error) {
            return { error: result.error };
          }

          return { data: undefined };
        },
        invalidatesTags: (result, error, { availabilityRuleId }) => [
          { type: 'BlockedTime', id: availabilityRuleId },
          { type: 'BlockedTimeList', id: 'LIST' },
        ],
      }),
    }),
    overrideExisting: false,
  });

export const {
  useListBlockedTimeRulesQuery,
  useCreateBlockedTimeRuleMutation,
  useUpdateBlockedTimeRuleMutation,
  useDeleteBlockedTimeRuleMutation,
} = blockedTimeApi;
